import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TextField } from '@mui/material';

import CardDialog from '~/components/CardDialog';
import { ConfirmModal } from '~/components/ConfirmModal';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import Checkbox from '~/components/ui/atoms/Checkbox';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import type { RollbackPreviewResult, RollbackPromotionParams } from '~/server_shared/openapi/data-contracts';
import { prettifyKey, reportAxiosError } from '~/Utils';

import BlockedSectionGroup from './BlockedSectionGroup';
import ChangeListByType from './ChangeListByType';
import {
  isRollbackBlockedUnacknowledgedError,
  previewRollback,
  promotionsBaseRoute,
  rollbackPromotion,
} from './promotionsUtils';
import type { IncomingPromotionRow, PromotionChange, PromotionTypeCounts } from './types';
import { resolutionFromConflicts, useConflictResolution } from './useConflictResolution';

const acknowledgeBlockedLabel = (types: string[]): string =>
  `I understand ${types.map(prettifyKey).join(', ')} will not be restored by this rollback. ` +
  'The pre-promotion snapshot is kept, so I can roll back again to restore it once the items above are fixed.';

interface RollbackPromotionDialogProps {
  /** The deployed promotion to roll back (null closes the dialog). */
  promotion: IncomingPromotionRow | null;
  organizationId: number;
  onClose: () => void;
  onChanged: () => void;
  /** Fired only when the rollback actually applies (mutates the target org's config) — the consumer uses it
   * to refresh the target org model (feature/config flags, Claim Bar/Sidebar, etc.), like approve's apply. */
  onApplied?: () => void;
}

/** Target-side rollback: restore the objects a deployed promotion changed back to their pre-apply values.
 * On open it previews the restore change list (a forward diff of the pre/post-apply snapshots) and any
 * post-deploy drift as conflicts; drift is resolved per-change exactly like approve (override / keep), reusing
 * the same conflict-resolution mechanics. Ineligible promotions (not the latest deployment, missing a
 * snapshot, un-appliable restore) come back rollbackable=false with an operator-facing reason. Gated on
 * SYSCONFIG_CONFIG_MANAGEMENT:FULL (the route enforces it). */
const RollbackPromotionDialog: React.FC<RollbackPromotionDialogProps> = ({
  promotion,
  organizationId,
  onClose,
  onChanged,
  onApplied,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const requestUuid = promotion?.request_uuid ?? null;
  const baseRoute = promotionsBaseRoute(organizationId);

  const [preview, setPreview] = useState<RollbackPreviewResult | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPreviewError, setIsPreviewError] = useState(false);
  const [rollbackReason, setRollbackReason] = useState('');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The operator's acknowledgment that the blocked sections are not restored now; required while any are listed.
  const [isBlockedAcknowledged, setIsBlockedAcknowledged] = useState(false);
  // A rollback was refused because the blocked set changed since the preview: shown over the fresh preview.
  const [isBlockedSetChanged, setIsBlockedSetChanged] = useState(false);
  // Bumped to preview again (after that refusal) without closing the dialog.
  const [previewRun, setPreviewRun] = useState(0);

  // The restore change list is deterministic (a diff of two immutable snapshots), so it's stable across a
  // re-drift — only the conflicts refresh. Memoize it so the resolution hook's memos keep stable inputs.
  const changes = useMemo(() => (preview?.changes ?? []) as PromotionChange[], [preview]);
  const blockedSections = preview?.blocked_sections ?? [];
  const cr = useConflictResolution(changes);
  const { enterResolution, reset } = cr;

  // Preview on open: POST the read-only preview (no GET detail endpoint exists for a rollback). Conflicts
  // surface here — the preview already runs detect_conflicts — so enter the resolution view straight away.
  useEffect(() => {
    if (!requestUuid) {
      return undefined;
    }

    let ignore = false;
    reset();
    setPreview(null);
    setIsBlockedAcknowledged(false);
    setIsPreviewError(false);
    setIsPreviewLoading(true);
    previewRollback(baseRoute, requestUuid)
      .then((result) => {
        if (ignore) {
          return;
        }
        setPreview(result);
        const next = resolutionFromConflicts(result);
        if (next) {
          enterResolution(next, { wasResolve: false });
        }
      })
      .catch(async (error) => {
        // A superseded preview (dialog closed / promotion switched) sets ignore — discard its failure
        // silently so a cancelled request never reaches the generic error modal / Sentry.
        if (ignore) {
          return;
        }
        setIsPreviewError(true);
        await reportAxiosError(error);
      })
      .finally(() => {
        if (!ignore) {
          setIsPreviewLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [requestUuid, baseRoute, reset, enterResolution, previewRun]);

  const close = () => {
    setPreview(null);
    setRollbackReason('');
    setIsConfirmOpen(false);
    setIsBlockedAcknowledged(false);
    setIsBlockedSetChanged(false);
    reset();
    onClose();
  };

  const handleRollback = async () => {
    if (!requestUuid) {
      return;
    }
    try {
      setIsSubmitting(true);
      const body: RollbackPromotionParams = {
        request_uuid: requestUuid,
        rollback_reason: rollbackReason.trim() || null,
        // The sections the operator acknowledged are not restored now (Roll back is disabled until they do).
        acknowledged_blocked_configuration_types: blockedSections.map((section) => section.configuration_type),
      };
      // In resolution → echo the reviewer's per-change decisions + the reported hash (stale-override guard);
      // a clean preview posts neither, so the manager restores every change.
      if (cr.inResolution) {
        body.override_change_ids = cr.overrideChangeIds;
        body.expected_conflict_hash = cr.conflictHash;
      }
      const info = await rollbackPromotion(baseRoute, body);
      const next = resolutionFromConflicts(info);
      if (next) {
        // The target drifted again between preview and apply — nothing was restored; re-report and re-decide.
        enterResolution(next, { wasResolve: true });
        // The reviewer clicked the footer, so the refreshed banner and decision cards are off-screen above.
        dialogRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      // Applied: the target org's config was mutated, so refresh its model in addition to the lists.
      onApplied?.();
      onChanged();
      close();
    } catch (error) {
      if (isRollbackBlockedUnacknowledgedError(error)) {
        // Nothing was restored: the blocked sections changed since the preview. Preview again and re-acknowledge.
        setIsBlockedSetChanged(true);
        setPreviewRun((run) => run + 1);
        return;
      }
      await reportAxiosError(error);
    } finally {
      setIsConfirmOpen(false);
      setIsSubmitting(false);
    }
  };

  const deployedWhen = promotion?.resolved_at ? serverDateTimeToLocal(promotion.resolved_at) : null;
  const deployedBy = promotion?.approver_display_name ?? null;
  const overrideCount = cr.materialConflictIds.filter((id) => cr.decisions[id] === 'override').length;

  const resolutionBanner = (() => {
    if (cr.isStaleRefresh) {
      return {
        title: 'Target changed again',
        note: 'The target changed again before the rollback could apply — nothing was restored. The changes below have been refreshed; re-check your decisions and try again.',
      };
    }
    if (cr.conflictCount > 0) {
      return {
        title: 'Conflicts detected',
        note: `The target changed since this promotion was deployed — ${cr.conflictCount} object(s) need a decision because restoring would overwrite a value someone edited since. For each, choose “Use incoming” to restore the pre-deploy value, or “Keep current” to leave it as it is now. Everything else is restored automatically.`,
      };
    }

    return {
      title: 'Target changed — nothing to decide',
      note: 'The target changed since this promotion was deployed, but restoring would not overwrite those edits. Review the changes below and roll back — everything is restored automatically.',
    };
  })();

  return (
    <CardDialog
      contentRef={dialogRef}
      isDialog
      title="Roll back promotion"
      open={Boolean(requestUuid)}
      onClose={close}
      preventClose={isSubmitting}
      fullWidth
      maxWidth="md"
      footerActions={
        <>
          <Button variant="text" onClick={close}>
            Close
          </Button>
          {preview?.rollbackable && (
            <Button
              variant="contained"
              color="primary"
              disabled={
                isSubmitting ||
                (cr.inResolution && !cr.allConflictsResolved) ||
                (blockedSections.length > 0 && !isBlockedAcknowledged)
              }
              onClick={() => setIsConfirmOpen(true)}
            >
              {cr.inResolution ? 'Resolve & roll back' : 'Roll back'}
            </Button>
          )}
        </>
      }
    >
      <LoadingSwitch isLoading={isPreviewLoading} isError={isPreviewError}>
        {preview &&
          (!preview.rollbackable ? (
            <div className="flex flex-col gap-16 py-8">
              <AlertBanner
                withIcon
                alertType={AlertBanner.ALERT_TYPES.INFO}
                title="Can’t roll back"
                note={preview.blocked_reason ?? 'This promotion can’t be rolled back.'}
              />
              {/* A second rollback refused because the sections it would restore are still blocked lists why. */}
              {blockedSections.map((section) => (
                <BlockedSectionGroup key={section.configuration_type} section={section} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-16 py-8">
              <span>
                Restore the configuration this promotion changed back to how it was before it was deployed
                {deployedWhen ? ` on ${deployedWhen}` : ''}
                {deployedBy ? `, by ${deployedBy}` : ''}.
              </span>

              {isBlockedSetChanged && (
                <AlertBanner
                  withIcon
                  alertType={AlertBanner.ALERT_TYPES.WARNING}
                  title="Blocked sections changed"
                  note="The sections this rollback can’t restore changed since the preview, so nothing was restored. Review them below, acknowledge them again and roll back."
                />
              )}

              {cr.inResolution && (
                <AlertBanner
                  withIcon
                  alertType={AlertBanner.ALERT_TYPES.WARNING}
                  title={resolutionBanner.title}
                  note={resolutionBanner.note}
                />
              )}

              <ChangeListByType
                changes={changes}
                summary={(preview.summary ?? []) as PromotionTypeCounts[]}
                conflicts={cr.inResolution ? cr.conflictMap : undefined}
                conflictDecisions={cr.inResolution ? cr.decisions : undefined}
                onResolveConflict={cr.inResolution ? cr.handleResolveConflict : undefined}
                blockedSections={blockedSections}
              />

              {blockedSections.length > 0 && (
                <Checkbox
                  label={acknowledgeBlockedLabel(blockedSections.map((section) => section.configuration_type))}
                  checked={isBlockedAcknowledged}
                  disabled={isSubmitting}
                  onChange={(_e, checked) => setIsBlockedAcknowledged(checked)}
                />
              )}

              <TextField
                id="rollback-reason"
                label="Reason for rolling back (optional)"
                value={rollbackReason}
                onChange={(e) => setRollbackReason(e.target.value)}
                disabled={isSubmitting}
                multiline
                rows={2}
                fullWidth
                variant="standard"
              />
            </div>
          ))}
      </LoadingSwitch>

      <ConfirmModal
        isOpen={isConfirmOpen}
        isSubmitting={isSubmitting}
        onClose={() => setIsConfirmOpen(false)}
        title="Roll back promotion"
        contentText={`Restore the objects this promotion changed to their pre-deploy values${
          cr.inResolution
            ? `, restoring the ${overrideCount} conflict(s) you chose to override and keeping the rest as they are now`
            : ''
        }? This applies as a new deployment and can’t itself be rolled back.`}
        primaryButtonName="Confirm rollback"
        secondaryButtonName="Cancel"
        onPrimaryBtnClick={handleRollback}
      />
    </CardDialog>
  );
};

export default RollbackPromotionDialog;
